import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { userService } from "../services/userService";
import { userDisplayId } from "./UserCommonStore";
import { User } from "../../../api/graphql";
import { roleDisplayId } from "../../role/store/RoleCommonStore";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { Language } from "../../../api/graphql";
import { IdCardType } from "../../../api/graphql";
import { Gender } from "../../../api/graphql";
import { roleService } from "../../role/services/roleService";
import { InitializeParams } from "../../../store/types";

export class UserViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = userService;
  userInstance: User | null = null;
  cardState: Record<string, boolean> = { main: true, details: true };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  emailValue: string = "";
  roleService = roleService;
  roleSearchValue: string = "";
  roleIdValue: string = "";
  roleSelectedValue: { displayId: string; id: string } | null = null;
  languageValue: Language = "" as Language;
  avatarFiles: CrudRows = {};
  tenantDisplayNameValue: string = "";
  activeValue: boolean = false;
  cellphoneValue: string = "";
  twoFaseAuthValue: boolean = false;
  idCardTypeValue: IdCardType | "" = "" as IdCardType | "";
  idCardValue: string = "";
  genderValue: Gender | "" = "" as Gender | "";
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadUserById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.userOne({ id });
    this.userInstance = result.userOne;
    if (this.userInstance) {
      this.loadValues(this.userInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.userInstance) {
      return userDisplayId(this.userInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.UserSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "role") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.roleOne({ id: params.returnData.resultId });
        const role = result.roleOne;
        this.roleSelectedValue = role;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get roleValue() {
    if (this.roleSelectedValue) {
      return {
        id: this.roleSelectedValue.id,
        displayId: roleDisplayId(this.roleSelectedValue, this.langStore),
      };
    }
    return null;
  }

  getLanguageOptions() {
    return this.langStore.LanguageOptions;
  }

  getIdCardTypeOptions() {
    return this.langStore.IdCardTypeOptions;
  }

  getGenderOptions() {
    return this.langStore.GenderOptions;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "User",
      fieldName: fieldName,
      instanceId: this.userInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "User",
        fieldName: fieldName,
        instanceId: this.userInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.userInstance = null;
    this.nameValue = "";
    this.emailValue = "";
    this.roleSearchValue = "";
    this.roleIdValue = "";
    this.languageValue = "" as Language;
    this.avatarFiles = {};
    this.tenantDisplayNameValue = "";
    this.activeValue = false;
    this.cellphoneValue = "";
    this.twoFaseAuthValue = false;
    this.idCardTypeValue = "" as IdCardType | "";
    this.idCardValue = "";
    this.genderValue = "" as Gender | "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelUser() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("user_update");
  }

  editUser() {
    const id = this.userInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/user/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printUser(handlePrint: () => void): void {
    handlePrint();
  }

  *closeUser(): any {
    yield this.close();
  }

  loadValues(instance: User) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.emailValue = instance.email;
    this.roleSelectedValue = instance.role ? instance.role : null;
    this.languageValue = instance.language;
    this.avatarFiles = instance.avatar_data
      ? {
          [instance.avatar_data.name]: {
            name: instance.avatar_data.name,
            formattedSize: formatBytes(instance.avatar_data?.size || 0),
            mimetype: instance.avatar_data?.mimetype,
            id: instance.avatar_data.name,
            __status__: "original",
          },
        }
      : {};
    this.tenantDisplayNameValue = instance.tenantDisplayName || "";
    this.activeValue = instance.active;
    this.cellphoneValue = instance.cellphone || "";
    this.twoFaseAuthValue = instance.twoFaseAuth || false;
    this.idCardTypeValue = instance.idCardType || ("" as IdCardType | "");
    this.idCardValue = instance.idCard || "";
    this.genderValue = instance.gender || ("" as Gender | "");
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadUserById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
